import { useCallback, useEffect, useRef, useState } from "react";

interface UseWebcamProps {
  onDisconnect?: () => void;
  onConnect?: () => void;
  onUnavailable?: () => void;
  defaultWebcamId?: string;
}

/**
 * a hook for using the webcam
 * Uses the browser's built in navigator.mediaDevices.getUserMedia
 * the video element shows a stream of images from the web camera
 * the canvas element is for taking one of those stream images and saving it here
 */
export const useWebcam = (props?: UseWebcamProps) => {
  const { onConnect, onDisconnect, onUnavailable, defaultWebcamId } =
    props || {};
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);

  const [isCapturing, setIsCapturing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoTracks, setVideoTracks] = useState<MediaStreamTrack[]>();
  const [selectedVideoTrack, setSelectedVideoTrack] =
    useState<MediaStreamTrack | null>(null);
  const [selectedVideoSettings, setSelectedVideoSettings] =
    useState<MediaTrackSettings | null>(null);

  const [initialized, setInitialized] = useState(false);

  /**
   * Returns a dataUrl of the webcam screenshot
   */
  const getWebcamScreenshot = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const video = videoRef.current;

    if (!canvas || !context || !video) return null;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg");

    return dataUrl;
  }, []);

  const initializeWebcam = useCallback(async () => {
    try {
      setIsLoading(true);
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      videoStreamRef.current = newStream;
    } catch (e) {
      setVideoTracks([]);
      setSelectedVideoTrack(null);
      setIsCapturing(false);
      setIsLoading(false);
      onUnavailable?.();
      return false;
    }

    return true;
  }, [onUnavailable]);

  const startCapturing = useCallback(() => {
    if (!videoStreamRef.current) {
      return;
    }

    const stream = videoStreamRef.current;

    const handleDisconnect = () => {
      setVideoTracks([]);
      setSelectedVideoTrack(null);
      setIsCapturing(false);
      onDisconnect?.();
    };

    stream.getVideoTracks().forEach((track) => {
      track.onended = handleDisconnect;
    });

    const video = videoRef.current;

    // if just checking if camera works then set to true
    if (!video) {
      setIsCapturing(true);
      onConnect?.();
      setIsLoading(false);
      return;
    }

    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;

      if (canvasRef.current) {
        const canvas = canvasRef.current;

        // apparently canvas has an internal and external resolution for width and height
        // this assigning the width and height directly sets the internal resolution
        // setting width and height via css sets the external resolution
        canvas.height = height;
        canvas.width = width;
      }

      // Handle play() promise errors gracefully
      video
        .play()
        .then(() => {
          setIsCapturing(true);
          onConnect?.();
          setIsLoading(false);
        })
        .catch((error) => {
          if (error.name === "AbortError") {
            console.warn("[webcam] Playback aborted:", error.message);
          } else {
            console.error("[webcam] Playback error:", error);
          }
        });
    };

    const newVideoTracks = stream.getVideoTracks?.() ?? [];
    setVideoTracks(newVideoTracks);

    let videoTrack: MediaStreamTrack | null = newVideoTracks?.[0] ?? null;

    if (defaultWebcamId) {
      videoTrack =
        newVideoTracks.find(
          (videoTrack) => videoTrack.id === defaultWebcamId,
        ) ?? null;
    }

    setSelectedVideoTrack(videoTrack);
    setSelectedVideoSettings(videoTrack?.getSettings() ?? null);

    video.srcObject = stream;
  }, [onDisconnect, onConnect, defaultWebcamId]);

  const stopCapturing = useCallback(() => {
    if (videoStreamRef.current) {
      videoStreamRef.current.getVideoTracks().forEach((track) => track.stop());
    }

    setIsCapturing(false);
  }, []);

  useEffect(() => {
    setInitialized(false);
    let isMounted = true;

    initializeWebcam().then((res) => {
      if (res && isMounted) {
        setInitialized(true);
      }
    });

    return () => {
      isMounted = false;
      stopCapturing();
    };
  }, [initializeWebcam, stopCapturing]);

  useEffect(() => {
    if (initialized) {
      startCapturing();
    }
  }, [initialized, startCapturing]);

  return {
    isLoading,
    isCapturing,
    /**
     * a ref to a \<video\> element
     *
     */
    videoRef,
    /**
     * a ref to a \<canvas\> element
     */
    canvasRef,
    videoTracks,
    selectedVideoTrack,
    selectedVideoSettings,
    setSelectedVideoTrack,
    getWebcamScreenshot,
  };
};
